import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import Input from "./Input";
import Select from "./Select";
import clientRateOptionSets from "../../options/clientrate.json";
import { getProgramModalities } from "../services/soar";
import {
  getFieldText,
  getFieldName,
  updateEntity,
  createEntity,
} from "./../../scripts/helperScripts";
import { DateTime } from "luxon";

function ClientRateModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  setClientId,
  clientProgram,
  fosterHomePrograms,
  modalities,
  updateModalities,
  updateRates,
  currentRate,
}) {
  const clientProgramAdmitDate = useMemo(() => {
    return DateTime.fromISO(getFieldText(clientProgram?.ccx_admitdate));
  }, [clientProgram?.ccx_admitdate]);

  const [clientRate, setClientRate] = useState({});
  const [clientProgramOptions, setClientProgramOptions] = useState([]);
  const [clientProgramsLoading, setClientProgramsLoading] = useState(false);
  const [careModalityOptions, setCareModalityOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = useMemo(() => {
    const min = clientProgramAdmitDate.minus({
      days: 1,
    });

    return {
      sor_billingrate: Joi.when("sor_reasonforrateupdate", {
        is: Joi.string()
          .required()
          .valid("186460002", "186460003", "186460004"),
        then: Joi.number().required().min(1).max(1000).label("Billing Rate"),
      }),
      sor_adminrate: Joi.when("sor_reasonforrateupdate", {
        is: Joi.string()
          .required()
          .valid("186460002", "186460003", "186460004"),
        then: Joi.number()
          .required()
          .min(0)
          .max(Joi.ref("sor_billingrate"))
          .label("Admin Rate"),
      }),
      sor_reasonforrateupdate: Joi.string()
        .required()
        .label("Reason For Change"),
      sor_effdate: Joi.date()
        .min(min.toISO())
        .max(
          clientProgramAdmitDate > DateTime.now()
            ? clientProgramAdmitDate.toISO()
            : "now"
        )
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "any.required":
                error.message = `"Effective Date" is required`;
                break;
              case "date.max":
                const limit = DateTime.fromMillis(
                  Date.parse(error.context.limit)
                );
                error.message = `"Effective Date" cannot be future-dated beyond ${limit.toLocaleString(
                  DateTime.DATE_SHORT
                )}`;
                break;
              case "date.min":
                error.message = `"Effective Date" cannot be before ${DateTime.fromMillis(
                  Date.parse(error.context.limit)
                )
                  .plus({ days: 1 })
                  .toLocaleString(DateTime.DATE_SHORT)}`;
                break;
              default:
                error.message = `"Effective Date" is invalid`;
                break;
            }
          });
          return errors;
        }),
      careModality: Joi.string()
        .label("Care Modality")
        .when("sor_reasonforrateupdate", {
          is: "186460000",
          then: Joi.string().required(),
        })
        .when("sor_reasonforrateupdate", {
          is: "186460001",
          then: Joi.string().required(),
        }),
      clientProgram: Joi.string()
        .label("Program Unit")
        .when("sor_reasonforrateupdate", {
          is: "186460000",
          then: Joi.string().required(),
        })
        .when("sor_reasonforrateupdate", {
          is: "186460001",
          then: Joi.string().required(),
        }),
    };
  }, [clientProgramAdmitDate]);

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const data = { ...clientRate };
    data[input.name] = input.value;
    setClientRate(data);

    setErrors(errs);
  };

  const handleReasonChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (input.value) {
      setErrors((errs) => {
        const obj = { ...errs };
        delete obj.careModality;
        delete obj.clientProgram;
        delete obj.sor_billingrate;
        delete obj.sor_adminrate;
        return obj;
      });
    }
  };

  const handleBillingRateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    const errorMessage = validateProperty(
      {
        name: "sor_adminrate",
        value: clientRate.sor_adminrate,
      },
      [{ name: "sor_billingrate", value: input.value }]
    );

    setErrors((errs) => {
      const obj = { ...errs };

      if (errorMessage) {
        obj.sor_adminrate = errorMessage;
      } else {
        delete obj.sor_adminrate;
      }

      return obj;
    });
  };

  const handleEffectiveDateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (input.value) {
      setClientProgramOptions([]);
      setClientProgramsLoading(true);
      getProgramModalities(clientId, input.value).then((response) => {
        if (response && isMountedRef.current) {
          console.log("Get Program Modalities: ", response);
          const uniqueIds = [];
          const options = [];
          response.forEach((modality) => {
            if (
              !uniqueIds.includes(
                `${getFieldText(modality.sor_caremodality)}.${getFieldText(
                  modality.sor_programunit
                )}`
              )
            ) {
              uniqueIds.push(
                `${getFieldText(modality.sor_caremodality)}.${getFieldText(
                  modality.sor_programunit
                )}`
              );

              const option = {
                _id: getFieldText(modality.sor_caremodality),
                name: `${getFieldName(
                  modality.sor_caremodality
                )} - ${DateTime.fromISO(
                  getFieldText(modality.sor_effdate)
                ).toLocaleString(DateTime.DATE_SHORT)}${
                  modality.sor_expdate
                    ? ` - ${DateTime.fromISO(
                        getFieldText(modality.sor_expdate)
                      ).toLocaleString(DateTime.DATE_SHORT)}`
                    : ""
                }`,
                programUnit: getFieldText(modality.sor_programunit),
              };

              options.push(option);
            }
          });

          setCareModalityOptions(options);
          console.log("Care Modality Options: ", options);
          let availableClientPrograms = [];
          if (fosterHomePrograms && fosterHomePrograms.length > 0) {
            availableClientPrograms = fosterHomePrograms
              .filter(
                (program) =>
                  DateTime.fromISO(getFieldText(program.ccx_admitdate)) <=
                    DateTime.fromISO(input.value) &&
                  (program.ccx_dischargedate === undefined ||
                    DateTime.fromISO(getFieldText(program.ccx_dischargedate)) >=
                      DateTime.fromISO(input.value))
              )
              .map((program) => {
                return {
                  _id: program.ccx_client_programid,
                  name: `${getFieldName(
                    program.ccx_programunit
                  )} - ${DateTime.fromISO(
                    getFieldText(program.ccx_admitdate)
                  ).toLocaleString(DateTime.DATE_SHORT)}`,
                  programUnit: getFieldText(program.ccx_programunit),
                  clientProgramName: program.ccx_name,
                };
              });
          }
          if (
            options
              .map((option) => option.programUnit)
              .includes(getFieldText(clientProgram.ccx_programunit)) &&
            DateTime.fromISO(getFieldText(clientProgram.ccx_admitdate)) <=
              DateTime.fromISO(input.value) &&
            (clientProgram.ccx_dischargedate === undefined ||
              DateTime.fromISO(getFieldText(clientProgram.ccx_dischargedate)) >=
                DateTime.fromISO(input.value))
          ) {
            availableClientPrograms.push({
              _id: clientProgram.ccx_client_programid,
              name: `${getFieldName(
                clientProgram.ccx_programunit
              )} - ${DateTime.fromISO(
                getFieldText(clientProgram.ccx_admitdate)
              ).toLocaleString(DateTime.DATE_SHORT)}`,
              programUnit: getFieldText(clientProgram.ccx_programunit),
              clientProgramName: clientProgram.ccx_name,
            });
          }
          setClientProgramOptions(availableClientPrograms);

          if (availableClientPrograms.length === 1) {
            setClientRate((cr) => {
              const obj = { ...cr };
              obj.clientProgram = availableClientPrograms[0]._id;
              return obj;
            });
          } else {
            // check for corresponding Foster Homes program if selected program is TFC
            const fosterHomeClientProgram = availableClientPrograms.filter(
              (program) => {
                let name = clientProgram.ccx_name;

                if (
                  getFieldName(clientProgram["unit.ccx_programtype"]) === "TFC"
                ) {
                  name = getFieldName(clientProgram.ccx_programunit).replace(
                    ` - ${getFieldName(clientProgram["unit.ccx_program"])}`,
                    ""
                  );
                }

                return program.name.includes(name);
              }
            )[0];
            if (fosterHomeClientProgram) {
              setClientRate((cr) => {
                const obj = { ...cr };
                obj.clientProgram = fosterHomeClientProgram._id;
                return obj;
              });
            }
          }

          console.log("Available Program Units: ", availableClientPrograms);
        }

        setClientProgramsLoading(false);
      });
    }
  };

  const validateProperty = ({ name, value }, dependencies = []) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    dependencies.forEach((d) => {
      obj[d.name] = d.value;
      schema[d.name] = joiSchema[d.name];
    });
    const { error } = Joi.validate(obj, schema);
    return error?.details[0].message;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(clientRate, joiSchema, options);

    let validationErrors = {};

    if (error)
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;

    if (
      getFieldName(clientProgram["unit.ccx_programtype"]) === "TFC" &&
      clientProgram["fac.state"] !== "Alabama" &&
      clientRate.sor_reasonforrateupdate === "186460000"
    ) {
      if (
        clientRate.sor_billingrate === undefined ||
        clientRate.sor_billingrate === ""
      )
        validationErrors.sor_billingrate = `"Billing Rate" is required`;
      if (
        clientRate.sor_adminrate === undefined ||
        clientRate.sor_adminrate === ""
      )
        validationErrors.sor_adminrate = `"Admin Rate" is required`;
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const handleSave = () => {
    if (setClientId) {
      setClientId(null);
    }

    const rateObj = {
      sor_contact: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: getFieldText(clientProgram.ccx_patient),
        },
      },
      sor_name: {
        type: "string",
        value: `${getFieldName(clientProgram.ccx_patient)}-${new Date(
          clientRate.sor_effdate
        ).toDateString()}`,
      },
      sor_reasonforrateupdate: {
        type: "optionset",
        value: parseInt(clientRate.sor_reasonforrateupdate),
      },
      sor_billingrate: {
        type: "decimal",
        value: clientRate.sor_billingrate,
      },
      sor_adminrate: {
        type: "decimal",
        value: clientRate.sor_adminrate,
      },
      sor_maintenancerate: {
        type: "decimal",
        value:
          parseFloat(clientRate.sor_billingrate) -
          parseFloat(clientRate.sor_adminrate),
      },
      sor_effdate: {
        type: "datetime",
        value: new Date(clientRate.sor_effdate),
      },
    };

    const toastId = toast.loading("Client Rate is being created...", {
      type: toast.TYPE.INFO,
    });
    createEntity(
      "sor_fosterparentreimbursement",
      JSON.stringify(rateObj),
      safyInfo,
      getFieldName(clientProgram.ccx_company).split(" of ")[1]
    )
      .then(() => {
        updateRates && updateRates(true);
        if (
          clientRate.sor_reasonforrateupdate === "186460000" ||
          clientRate.sor_reasonforrateupdate === "186460001"
        ) {
          const clientProgramOption = clientProgramOptions.find(
            (option) => option._id === clientRate.clientProgram
          );
          const obj = {
            ccx_name: {
              type: "string",
              value: clientProgramOption.clientProgramName,
            },
            ccx_clientprogramid: {
              type: "entity",
              value: {
                entityName: "ccx_client_program",
                entityId: clientProgramOption._id,
              },
            },
            ccx_caremodality: {
              type: "optionset",
              value: parseInt(clientRate.careModality),
            },
            sor_contact: {
              type: "entity",
              value: {
                entityName: "contact",
                entityId: clientId,
              },
            },
            ccx_startdate: {
              type: "datetime",
              value: new Date(clientRate.sor_effdate),
            },
          };

          toast.update(toastId, {
            render:
              "Client Rate has been created. Care Modality is being created...",
          });
          createEntity(
            "ccx_caremodality",
            JSON.stringify(obj),
            safyInfo,
            getFieldName(clientProgram.ccx_company).split(" of ")[1]
          )
            .then(() => {
              if (modalities) {
                const prevModality = modalities.filter(
                  (modality) =>
                    modality.ccx_enddate == null &&
                    getFieldText(modality.ccx_clientprogramid) ===
                      clientProgramOption._id
                )[0];
                if (prevModality != null) {
                  const prevModalityObj = {
                    ccx_enddate: {
                      type: "datetime",
                      value: new Date(clientRate.sor_effdate),
                    },
                  };

                  toast.update(toastId, {
                    render:
                      "Care Modality has been created. Expiring previous Care Modality...",
                  });
                  updateEntity(
                    "ccx_caremodality",
                    prevModality.ccx_caremodalityid,
                    JSON.stringify(prevModalityObj),
                    safyInfo
                  )
                    .then(() => {
                      toast.update(toastId, {
                        render: "Previous Care Modality has been expired.",
                        type: toast.TYPE.SUCCESS,
                        isLoading: false,
                        autoClose: 5000,
                        closeOnClick: true,
                      });
                      updateModalities && updateModalities(true);
                    })
                    .catch(() => {
                      toast.update(toastId, {
                        render:
                          "An error occurred while expiring the previous Care Modality.",
                        type: toast.TYPE.ERROR,
                        isLoading: false,
                        autoClose: 5000,
                        closeOnClick: true,
                      });
                    });
                } else {
                  toast.update(toastId, {
                    render: "Care Modality has been created.",
                    type: toast.TYPE.SUCCESS,
                    isLoading: false,
                    autoClose: 5000,
                    closeOnClick: true,
                  });
                  updateModalities && updateModalities(true);
                }
              } else {
                toast.update(toastId, {
                  render: "Care Modality has been created.",
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
                updateModalities && updateModalities(true);
              }
            })
            .catch(() => {
              toast.update(toastId, {
                render: "An error occurred while creating the Care Modality.",
                type: toast.TYPE.ERROR,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
            });
        }

        toast.update(toastId, {
          render: "Client Rate has been created.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      })
      .catch(() => {
        toast.update(toastId, {
          render: "An error occurred while creating the Client Rate.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      });
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    setClientRate({});
    setClientProgramOptions([]);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const maintenanceRate = useMemo(() => {
    if (!clientRate.sor_billingrate || !clientRate.sor_adminrate) {
      return "";
    }

    const rate =
      parseFloat(clientRate.sor_billingrate) -
      parseFloat(clientRate.sor_adminrate);

    return rate.toFixed(2);
  }, [clientRate]);

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Client Rate/Care Modality</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Select
              name="sor_reasonforrateupdate"
              label="Reason for Change"
              options={clientRateOptionSets.sor_reasonforrateupdate}
              onChange={handleReasonChange}
              error={errors.sor_reasonforrateupdate}
            />
            <Input
              name="sor_effdate"
              label="Effective Date"
              type="date"
              error={errors.sor_effdate}
              onChange={handleEffectiveDateChange}
            />
            {(clientRate.sor_reasonforrateupdate === "186460000" ||
              clientRate.sor_reasonforrateupdate === "186460001") && (
              <>
                {clientRate.sor_effdate !== undefined &&
                  clientRate.sor_effdate !== "" &&
                  clientProgramOptions.length > 0 &&
                  !clientProgramsLoading && (
                    <Select
                      name="clientProgram"
                      label="Program Unit"
                      options={clientProgramOptions}
                      value={clientRate.clientProgram}
                      onChange={handleChange}
                      error={errors.clientProgram}
                      disabled={
                        clientRate.sor_effdate === undefined ||
                        clientRate.sor_effdate === ""
                      }
                    />
                  )}
                {clientRate.sor_effdate !== undefined &&
                  clientRate.sor_effdate !== "" &&
                  clientProgramOptions.length === 0 &&
                  !clientProgramsLoading && (
                    <div className="text-danger mb-2">
                      No Available Modalities for Client
                    </div>
                  )}
                {clientProgramsLoading && (
                  <div className="mb-2">Program Units loading...</div>
                )}
                <Select
                  name="careModality"
                  label="Care Modality"
                  options={careModalityOptions.filter((modality) =>
                    clientProgramOptions.length > 0 &&
                    clientRate.clientProgram !== undefined &&
                    clientRate.clientProgram !== ""
                      ? modality.programUnit ===
                        clientProgramOptions.filter(
                          (option) => option._id === clientRate.clientProgram
                        )[0].programUnit
                      : []
                  )}
                  value={clientRate.careModality}
                  onChange={handleChange}
                  error={errors.careModality}
                  disabled={
                    clientRate.clientProgram === undefined ||
                    clientRate.clientProgram === ""
                  }
                />
              </>
            )}
            {(clientRate.sor_reasonforrateupdate === "186460002" ||
              clientRate.sor_reasonforrateupdate === "186460003" ||
              clientRate.sor_reasonforrateupdate === "186460004" ||
              (clientRate.clientProgram !== undefined &&
                clientRate.clientProgram !== "" &&
                clientProgramOptions.length > 0 &&
                clientProgramOptions
                  .filter(
                    (option) => option._id === clientRate.clientProgram
                  )[0]
                  .clientProgramName.includes("Foster"))) && (
              <>
                <Input
                  name="sor_billingrate"
                  label="Billing Rate"
                  value={clientRate.sor_billingrate}
                  error={errors.sor_billingrate}
                  onChange={handleBillingRateChange}
                  placeholder={
                    currentRate !== undefined
                      ? getFieldText(currentRate.sor_billingrate)
                      : undefined
                  }
                />
                <Input
                  name="sor_adminrate"
                  label="Admin Rate"
                  value={clientRate.sor_adminrate}
                  error={errors.sor_adminrate}
                  onChange={handleChange}
                  placeholder={
                    currentRate !== undefined
                      ? getFieldText(currentRate.sor_adminrate)
                      : undefined
                  }
                />
                <Input
                  name="sor_maintenancerate"
                  label="Maintenance Rate"
                  value={maintenanceRate}
                  error={errors.sor_maintenancerate}
                  disabled
                  placeholder={
                    currentRate !== undefined
                      ? getFieldText(currentRate.sor_maintenancerate)
                      : undefined
                  }
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default ClientRateModal;
